import React from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { useTheme } from '../theme/ThemeProvider';

// Категориальные слоты (проверены валидатором для светлой и тёмной поверхности): 1 — синий, 2 — оранжевый
export const SERIES_COLORS = {
  light: ['#2a78d6', '#eb6834'],
  dark: ['#3987e5', '#d95926'],
};

/** Дни без данных → нули: ось всегда покрывает весь период, а не только дни с операциями */
function fillDays(data, days, keys) {
  if (!days) return data;
  const byDate = new Map(data.map((row) => [row.date, row]));
  const out = [];
  // Дни операций — местные даты (date(tran_date)), поэтому и «сегодня» берём местное
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const row = byDate.get(date);
    out.push(Object.fromEntries([['date', date], ...keys.map((k) => [k, Number(row?.[k]) || 0])]));
  }
  return out;
}

/**
 * Динамика во времени: линии 2px, одна ось Y, легенда при ≥ 2 сериях, подсказка по наведению.
 * series: [{ key, label }]; formatValue — подпись значений (оси и подсказки); formatDate — подпись дня;
 * days — сколько последних дней показывать (пропуски заполняются нулями).
 */
export function TrendChart({ data, series, formatValue = (v) => v, formatDate = (d) => d, height = 260, days = 30 }) {
  const { isDark } = useTheme();
  const rows = fillDays(data, days, series.map((s) => s.key));
  const colors = SERIES_COLORS[isDark ? 'dark' : 'light'];
  const grid = isDark ? '#27272a' : '#f0f0f1';
  const axis = isDark ? '#a1a1aa' : '#71717a';

  return (
    <div>
      {series.length > 1 && (
        <ul className="mb-3 flex flex-wrap gap-4 text-xs text-zinc-600 dark:text-zinc-400">
          {series.map((s, i) => (
            <li key={s.key} className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: colors[i] }} aria-hidden="true" />
              {s.label}
            </li>
          ))}
        </ul>
      )}
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={grid} />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fill: axis, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              minTickGap={24}
            />
            <YAxis
              tickFormatter={formatValue}
              tick={{ fill: axis, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={72}
            />
            <Tooltip
              cursor={{ stroke: axis, strokeWidth: 1, strokeDasharray: '3 3' }}
              content={<ChartTooltip series={series} colors={colors} formatValue={formatValue} formatDate={formatDate} />}
            />
            {series.map((s, i) => (
              <Line
                key={s.key}
                type="linear"
                dataKey={s.key}
                name={s.label}
                stroke={colors[i]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: isDark ? '#18181b' : '#ffffff' }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label, series, colors, formatValue, formatDate }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs shadow-pop dark:border-zinc-700 dark:bg-zinc-900">
      <p className="mb-1 font-medium text-zinc-900 dark:text-zinc-100">{formatDate(label)}</p>
      {series.map((s, i) => {
        const item = payload.find((p) => p.dataKey === s.key);
        return (
          <p key={s.key} className="flex items-center gap-2 text-zinc-600 dark:text-zinc-300">
            <span className="h-2 w-2 rounded-full" style={{ background: colors[i] }} aria-hidden="true" />
            <span className="flex-1">{s.label}</span>
            <span className="tabular font-medium text-zinc-900 dark:text-zinc-100">{formatValue(item?.value ?? 0)}</span>
          </p>
        );
      })}
    </div>
  );
}
